<div class="pdv2" [attr.data-kind]="kind">
  <pla-main-nav-chips></pla-main-nav-chips>

  <!-- ============================ HERO / PRICING ============================ -->
  <section class="hero" id="sec-overview">
    <div class="wrap hero-grid">
      <!-- MOBILE identity block (stacked layout only): registration, identity
           line and pills directly ABOVE the photograph. The title and line
           duplicate the h1 / identity line, so they are hidden from assistive tech;
           the real h1 stays in the info column (visually hidden on mobile) so the
           document outline is unchanged. The info column's pills are hidden on
           mobile, so these are the only pills on a phone. -->
      <div class="hero-mobile-head">
        <div class="hero-reg plate-font" aria-hidden="true">{{ heroTitle }}</div>
        <p class="hero-mobile-id" *ngIf="identityLine" aria-hidden="true">{{ identityLine }}</p>
        <div class="hero-pills" *ngIf="heroPills.length">
          <pla-classification-pill
            *ngFor="let p of heroPills"
            [label]="p.label"
            [describe]="p.describe || ''"
            [variant]="p.kind === 'format' ? 'category' : 'arrangement'"
            size="sm"
            [href]="p.link || null"
            [as]="p.link ? 'link' : 'static'"
          ></pla-classification-pill>
        </div>
      </div>

      <!-- LEFT: registration, type, price / status, facts -->
      <div class="hero-left">
        <div class="hero-info">
        <!-- Registration is the dominant title, set in the authentic plate font
             (UKReg) as TEXT — not a plate badge. -->
        <h1 class="hero-reg plate-font">{{ heroTitle }}</h1>

        <!-- Identity line ("Issued September 2005 · Ipswich"). The format is
             stated once, by the pill below. -->
        <p class="hero-sub" *ngIf="identityLine">{{ identityLine }}</p>

        <!-- Deterministic, data-backed summary pills (format / arrangement /
             numeric). No inferred meaning is ever shown. The dateless format
             pill deep-links into the Dateless Reference (shared
             pla-classification-pill; build pack §8). -->
        <div class="hero-pills" *ngIf="heroPills.length">
          <pla-classification-pill
            *ngFor="let p of heroPills"
            [label]="p.label"
            [describe]="p.describe || ''"
            [variant]="p.kind === 'format' ? 'category' : 'arrangement'"
            size="sm"
            [href]="p.link || null"
            [as]="p.link ? 'link' : 'static'"
          ></pla-classification-pill>
        </div>

        <!-- Conditional intelligence line — only when a search word is present. -->
        <p class="hero-intel" *ngIf="intelligenceLine">
          Reads as <em>"{{ intelligenceLine }}"</em>
        </p>

        <!-- Approved plate description (plate knowledge base; dev2 / local
             only). Renders nothing when the plate has none. -->
        <pla-pdv2-hero-description
          [registration]="registrationString"
          [displayRegistration]="heroTitle"
        ></pla-pdv2-hero-description>

        <!-- Editorial status panel: first "what is the current state?"
             (heading / body / supporting), then "what can I do next?" as
             text-link actions. Replaces the old single green CTA. No historic
             sale prices, no DVLA phrasing. -->
        <div class="hero-status" *ngIf="heroStatusPanel as panel" [class.is-loading]="panel.loading">
          <ng-container *ngTemplateOutlet="statusBlock; context: { $implicit: panel }"></ng-container>
          <!-- DEV-only: dealer-listed plate also at auction — a second block of
               equal weight ("Listed for sale" then "At auction now"). -->
          <div class="hero-status-second" *ngIf="panel.secondary as second">
            <ng-container *ngTemplateOutlet="statusBlock; context: { $implicit: second }"></ng-container>
          </div>
        </div>

        <ng-template #statusBlock let-panel>
          <div
            class="hero-status-heading"
            [class.is-auction]="panel.headingTone === 'auction'"
            [class.is-alert]="panel.headingTone === 'alert'"
          >
            <i class="hero-live-dot" *ngIf="panel.headingTone" aria-hidden="true"></i>{{ panel.heading }}
          </div>
          <div
            class="hero-status-body"
            [class.is-statement]="panel.bodyTone === 'statement'"
          >
            {{ panel.body }}
          </div>
          <div class="hero-status-support" *ngIf="panel.supporting">
            {{ panel.supporting }}
          </div>
          <!-- DEV-only auction line (recent auction result): one quiet line. -->
          <div
            class="hero-auction-line"
            *ngIf="panel.auctionLine as al"
            [attr.data-tone]="al.tone"
          >
            <i class="hero-live-dot" *ngIf="al.tone === 'live' || al.tone === 'closing'" aria-hidden="true"></i>
            <span>{{ al.text }}</span>
            <a *ngIf="al.href" class="hero-auction-link" [href]="al.href" target="_blank" rel="noopener"
              >{{ al.linkLabel }} ↗</a
            >
          </div>
          <div class="hero-status-actions" *ngIf="panel.actions.length">
            <ng-container *ngFor="let a of panel.actions">
              <a
                *ngIf="a.href; else actionBtn"
                class="hero-status-action"
                [attr.data-emphasis]="a.emphasis"
                [href]="a.href"
                target="_blank"
                rel="noopener"
                >{{ a.label }} ↗</a
              >
              <ng-template #actionBtn>
                <button
                  type="button"
                  class="hero-status-action"
                  [attr.data-emphasis]="a.emphasis"
                  (click)="runHeroAction(a.handler)"
                >
                  {{ a.label }} →
                </button>
              </ng-template>
            </ng-container>
          </div>
        </ng-template>

        <!-- Optional market-insight line — hidden until backed by real data. -->
        <p class="hero-insight" *ngIf="marketInsight">{{ marketInsight }}</p>
        </div>

        <!-- Utility actions sit at the foot of the information column, level with
             the gallery's thumbnail row (pushed down by margin-top: auto). -->
        <pla-pdv2-hero-actions
          [registration]="registrationString"
          [formattedRegistration]="formattedRegistration"
          [searchTerm]="searchedTerm"
          [price]="fromPrice"
        ></pla-pdv2-hero-actions>

        <!-- "Sell a registration" is hidden for launch (unfinished journey). -->
      </div>

      <!-- RIGHT: gallery (main image + thumbnails). -->
      <div class="hero-right">
        <pla-pdv2-hero-gallery
          [registration]="formattedRegistration"
          [gallery]="heroGallery"
        ></pla-pdv2-hero-gallery>
        <!-- PlateViewer credit: a feature, not a watermark on the photograph. -->
        <p class="hero-credit" *ngIf="registrationString">
          Created with PlateViewer<span class="hero-credit-sep" aria-hidden="true">·</span><a
            [routerLink]="['/plate-viewer', 'plate', formattedRegistration || registrationString]"
            >Try another car</a
          >
        </p>

      </div>
    </div>
  </section>

  <!-- ========================= DEALER MARKETPLACE ========================= -->
  <pla-pdv2-marketplace
    [registration]="registrationString"
    [kind]="marketplaceKind"
    [dvla]="dvlaOffer"
    (priceRange)="onMarketRange($event)"
  ></pla-pdv2-marketplace>

  <!-- ====================== PLATE HISTORY TIMELINE ====================== -->
  <!-- Resale / provenance timeline — shown for non new-issue plates only. -->
  <!-- The add-photo form is projected into the timeline so it sits with the
       vehicle rows; each photo-less vehicle card opens it for that car. -->
  <pla-pdv2-timeline
    *ngIf="showTimeline"
    [events]="timelineEvents"
    [photoCta]="photoAddEnabled && currentStatus !== 'New Issue'"
    (addPhoto)="onAddPhoto($event)"
    (reviewPhoto)="onReviewPhoto($event)"
  >
    <pla-pdv2-photo-add
      *ngIf="photoAddEnabled && currentStatus !== 'New Issue'"
      [registration]="registrationString"
      [displayRegistration]="formattedRegistration"
      [knownCars]="knownCars"
      (photoAdded)="onPhotoAdded($event)"
    ></pla-pdv2-photo-add>
  </pla-pdv2-timeline>

  <pla-pdv2-photo-add
    *ngIf="photoAddEnabled && !showTimeline && currentStatus !== 'New Issue'"
    [registration]="registrationString"
    [displayRegistration]="formattedRegistration"
    [knownCars]="knownCars"
    (photoAdded)="onPhotoAdded($event)"
  ></pla-pdv2-photo-add>

  <!-- ====================== MARKET INFORMATION ========================= -->
  <!-- Precomputed PlateX index series (routed by plate format) rendered in the
       prototype graph style, with this plate's own verified sale markers. -->
  <pla-pdv2-market-info
    [registration]="heroTitle"
    [compactRegistration]="currentCompactRegistration"
    [formatKey]="currentFormatKey"
    [sales]="soldHistory"
    [askingPrice]="fromPrice"
    [marketLow]="marketRange?.low || null"
    [newIssue]="currentStatus === 'New Issue'"
  ></pla-pdv2-market-info>

  <!-- ====================== SIMILAR REGISTRATIONS ======================= -->
  <pla-pdv2-similar
    [registration]="registrationString"
    [formattedRegistration]="formattedRegistration"
    [searchTerm]="searchedTerm"
    [formatKey]="currentFormatKey"
  ></pla-pdv2-similar>

  <!-- ===================== PLATEFOLIO / OWN THIS REG ==================== -->
  <pla-pdv2-platefolio
    [registration]="registrationString"
    [formattedRegistration]="formattedRegistration"
    [status]="currentStatus"
    [hasPrice]="hasPrice"
    [hasListings]="!!marketRange"
  ></pla-pdv2-platefolio>
</div>
